import { FeventOne } from '@shared/api/fevent/fevent.interface';
import { User } from '@shared/api/user/user.interface';
import { UserRole } from '@shared/types';

/**
 * bugo의 user·bugoBrand 귀속 결정.
 *
 * - 일반 role(지도사·상조관리자 등): 기존 동작 그대로 — 로그인 계정 기준 귀속.
 * - 관리자(Admin)가 대리 작업할 때만: 상가(fevent)의 담당 지도사(fevent.user)·
 *   브랜드(fevent.bugoBrand)에 귀속. 관리자 계정으로 귀속하면 이후 담당 지도사가
 *   그 bugo를 재전송·수정할 때 백엔드 owningFilter(worker=bugo.user,
 *   agencyAdmin=bugo.bugoBrand) 밖이라 401로 막히기 때문.
 *
 * 백엔드도 admin 라우트(/admin/bugo·/admin/member) create에서 fevent 기준으로
 * 서버측 강제하므로(bugo.admin.service.ts) 여기는 표시·구버전 정합용 이중화다.
 */

const isAdmin = (userProfile?: User | null): boolean =>
  Boolean(userProfile?.roles?.includes(UserRole.Admin));

/** 상가(fevent)의 담당 user id — 관리자 대리 작업 시 member·bugo 귀속의 기준 */
export const feventUserId = (fevent: FeventOne): string | undefined => {
  const user = fevent.user as User | string | undefined;
  if (!user) return undefined;
  return typeof user === 'string' ? user : user._id;
};

const userProfileBrandId = (userProfile: User): string | undefined => {
  if (userProfile.roles.includes(UserRole.BugoAgencyAdmin)) {
    return userProfile.bugoAgencyAdminDetail?.bugoBrands[0]?._id;
  }
  //* 타입은 string이지만 populate된 조회 결과를 보관한 경우 객체일 수 있음 (useAuth와 동일 처리)
  const brand = userProfile.bugoBrand as string | { _id: string } | undefined;
  return typeof brand === 'string' ? brand : brand?._id;
};

/**
 * 생성 시 귀속.
 * - Admin: fevent 우선, 값이 없으면 로그인 계정 기준으로 폴백
 * - 그 외 role: 기존 로직 그대로 (로그인 계정 기준)
 */
export const bugoAttributionForCreate = (
  fevent: FeventOne,
  userProfile: User,
): { user: string; bugoBrand?: string } => {
  if (isAdmin(userProfile)) {
    return {
      user: feventUserId(fevent) ?? userProfile._id,
      bugoBrand: fevent.bugoBrand ?? userProfileBrandId(userProfile),
    };
  }
  return {
    user: userProfile._id,
    bugoBrand: userProfileBrandId(userProfile),
  };
};

/**
 * 수정 시 귀속 — Admin일 때만 상가 귀속으로 정렬(과거 관리자 귀속으로 생성된
 * bugo를 교정하는 효과). 일반 role은 빈 객체 → 기존 동작과 완전 동일.
 */
export const bugoAttributionForUpdate = (
  fevent: FeventOne,
  userProfile?: User | null,
): { user?: string; bugoBrand?: string } => {
  if (!isAdmin(userProfile)) return {};
  const out: { user?: string; bugoBrand?: string } = {};
  const userId = feventUserId(fevent);
  if (userId) out.user = userId;
  if (fevent.bugoBrand) out.bugoBrand = fevent.bugoBrand;
  return out;
};
